/* Published Frost Buddy photo placement. Studio drafts and manufacturing artwork
 * are separate: only the public Cup Library snapshot changes this projection. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.WeddingCupLibrary = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const ENDPOINT = 'https://images.jaxengraving.com/functions/v1/studio-cups';
  const PRODUCT_HANDLE = 'personalized-wedding-frost-buddy-universal-2-0-can-cooler';
  const id = value => typeof value === 'string' && /^[1-9]\d{0,23}$/.test(value);
  const uuid = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  const finite = (value, min, max) => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
  const fail = () => { throw new Error('wedding_cup_library_invalid'); };
  const rectangle = value => {
    if (!value || !finite(value.x, 0, 99) || !finite(value.y, 0, 99) || !finite(value.width, 1, 100) || !finite(value.height, 1, 100) || value.x + value.width > 100.00001 || value.y + value.height > 100.00001) fail();
    return Object.fromEntries(['x', 'y', 'width', 'height'].map(key => [key, value[key] / 100]));
  };
  const base = options => ({ photos: [...(options.photos || [])], variantPhotoMap: { ...(options.variantPhotoMap || {}) } });
  const enabled = options => options.productFamily === 'frostbuddy' && options.handle === PRODUCT_HANDLE && id(options.productId);

  function publishedPhoto(item, variant) {
    const profile = item.profile, side = profile.front, photo = item.photos?.[side?.photoId];
    if (!uuid(item.id) || !uuid(item.publishedRevision) || !Number.isSafeInteger(item.revision) || item.revision < 1 || profile.version !== 1 ||
        !side || !uuid(side.photoId) || !finite(side.widthMm, 1, 304.8) || !finite(side.heightMm, 6.35, 304.8) ||
        typeof side.measured !== 'boolean' || typeof side.previewOnly !== 'boolean' || (side.calibrationMode !== undefined && side.calibrationMode !== 'independent')) fail();
    const crop = rectangle(side.crop), stageArea = rectangle(side.area);
    if (side.calibrationMode !== 'independent' && Math.abs((side.widthMm / side.heightMm) / (stageArea.width / stageArea.height) - 1) > .005) fail();
    if (!photo || photo.id !== side.photoId || !['image/png', 'image/jpeg'].includes(photo.mime) || !Number.isInteger(photo.width) || !Number.isInteger(photo.height) || photo.width < 1 || photo.height < 1 || photo.width * photo.height > 40000000) fail();
    let url = new URL(photo.url);
    const endpoint = new URL(ENDPOINT);
    if (![endpoint.origin, 'https://xmpqogywwhxeaxujcdmj.supabase.co'].includes(url.origin) || url.pathname !== endpoint.pathname || url.username || url.password || url.hash || url.searchParams.size !== 3 || url.searchParams.get('action') !== 'photo' || url.searchParams.get('revisionId') !== item.publishedRevision || url.searchParams.get('photoId') !== photo.id) fail();
    if (photo.previewUrl) {
      const preview = new URL(photo.previewUrl);
      if (preview.origin !== 'https://cdn.shopify.com' || !preview.pathname.startsWith('/s/files/') || preview.username || preview.password || preview.hash || preview.searchParams.get('width') !== '1600' || [...preview.searchParams.keys()].some(key => !['width', 'v'].includes(key))) fail();
      url = preview;
    }
    // The Studio centers the cropped photo in its square stage using contain.
    const width = photo.width * crop.width, height = photo.height * crop.height, scale = 1 / Math.max(width, height);
    const stageCropRect = { x: (1 - width * scale) / 2, y: (1 - height * scale) / 2, width: width * scale, height: height * scale };
    return { id: `wedding-cup-library-${item.publishedRevision}-${profile.variantId}`, productId: 'frostbuddy', variantId: profile.variantId,
      size: variant.size, color: variant.color, side: 'front', width: photo.width, height: photo.height, crop, stageCropRect, stageArea,
      widthMm: side.widthMm, heightMm: side.heightMm, coordinateVersion: 2, approximate: true, previewOnly: true,
      calibrationMode: 'approximate-product-preview', calibrationSource: { type: 'published-cup-library', cupId: item.id, revisionId: item.publishedRevision,
        sourceAreaMm: { widthMm: side.widthMm, heightMm: side.heightMm }, measured: side.measured }, url: url.href };
  }

  function applyManifest(body, options) {
    const result = base(options);
    if (!enabled(options)) return result;
    const data = body?.data;
    if (body?.ok !== true || data?.version !== 1 || !Array.isArray(data.items) || !Array.isArray(data.suppressed) || data.items.length + data.suppressed.length > 500) fail();
    const variants = new Map(options.variants.map(variant => [String(variant.id), variant])), used = new Set();
    for (const item of data.suppressed) {
      if (!id(item?.productId) || !id(item.variantId)) fail();
      if (item.productId !== options.productId || !variants.has(item.variantId)) continue;
      if (used.has(item.variantId)) fail();
      used.add(item.variantId); delete result.variantPhotoMap[item.variantId];
    }
    for (const item of data.items) {
      const profile = item?.profile;
      if (!id(profile?.productId) || !id(profile.variantId)) fail();
      if (profile.productId !== options.productId || !variants.has(profile.variantId)) continue;
      if (used.has(profile.variantId) || profile.catalog?.handle !== options.handle) fail();
      used.add(profile.variantId);
      const photo = publishedPhoto(item, variants.get(profile.variantId));
      result.photos.push(photo); result.variantPhotoMap[profile.variantId] = photo.id;
    }
    return result;
  }

  async function load(options) {
    if (!enabled(options)) return { ...base(options), status: 'disabled' };
    const controller = new AbortController(), abort = () => controller.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.signal?.aborted) abort();
    let timer;
    try {
      // Race the complete response read as well as fetch, so a stalled body or
      // an implementation that ignores abort cannot hold the designer open.
      const request = (async () => {
        const response = await (options.fetchImpl || globalThis.fetch)(`${ENDPOINT}?action=manifest&assetHost=branded`, {
          signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store', headers: { Accept: 'application/json' }
        });
        if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('content-type') || '') || Number(response.headers.get('content-length')) > 2000000) fail();
        const text = await response.text();
        if (text.length > 2000000 || controller.signal.aborted) fail();
        return applyManifest(JSON.parse(text), options);
      })();
      const timeout = new Promise((_, reject) => { timer = setTimeout(() => { abort(); reject(new Error('wedding_cup_library_timeout')); }, options.timeoutMs ?? 5000); });
      return { ...await Promise.race([request, timeout]), status: 'loaded' };
    } catch {
      return { ...base(options), status: 'fallback' };
    } finally {
      clearTimeout(timer); options.signal?.removeEventListener('abort', abort);
    }
  }
  return Object.freeze({ applyManifest, load });
});
